import { FC, useCallback, useMemo } from 'react';
import { PersonsV3 } from '@frontend/api-person';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { Text, Avatar, Heading } from '@frontend/design-system';
import { TaskStatusLabels, TaskPriorityLabels } from '../../constants';
import { useTaskFormPatient } from '../../hooks';
import { useTaskCenterStore } from '../../provider';
import { Citation } from '../../types';
import { getFullName, getLabelFromMap, getPriorityIcon, getStatusIconName } from '../../utils';
import { TaskFormRow } from '../common';
import { TaskCitation } from '../create-task/citation/task-citation';
import { TaskDetailsPopoverMenu } from './task-details-popover.menu';
import { taskDetailsContainer, taskDescriptionStyle, taskTitleContainer, taskHeaderStyle } from './task-details.styles';

interface TaskDetailsProps {
  task: TaskCenterTypes.Task;
}

export const TaskDetailsRenderer: FC<TaskDetailsProps> = ({ task }) => {
  const { t } = useTranslation('task-center');
  const { locationsData, assignees, setTaskMode, setSelectedTask } = useTaskCenterStore([
    'locationsData',
    'assignees',
    'taskId',
    'setTaskMode',
    'setSelectedTask',
  ]);

  const locationName = useMemo(() => {
    return locationsData.find((loc) => loc.id === task.locationId)?.name;
  }, [locationsData, task.locationId]);
  const { patient } = useTaskFormPatient(task.patient?.id);
  const assignee = assignees.find((assign) => assign.userId === task.assignee);
  const status = getLabelFromMap(TaskStatusLabels, task.status);
  const taskPriority = getLabelFromMap(TaskPriorityLabels, task.priority);
  const priorityIcon = task.priority ? getPriorityIcon(task.priority).name : 'no-priority';
  const source: Citation = task?.metadata ? JSON.parse(task.metadata).source : undefined;
  const taskStatusIcon = getStatusIconName(task.status).name;

  const onClickEdit = useCallback(() => {
    setTaskMode('edit');
    setSelectedTask(task);
  }, [setTaskMode, setSelectedTask, task]);

  return (
    <div css={taskDetailsContainer}>
      <span css={taskTitleContainer}>
        <Heading textAlign='left' level={2} css={taskHeaderStyle}>
          {task.title}
        </Heading>
        {task.id && task.status && (
          <TaskDetailsPopoverMenu id={task.id} status={task.status} onClickEdit={onClickEdit} />
        )}
      </span>
      <TaskCitation source={task.source} sourceId={task.sourceId} citation={source} locationId={task.locationId} />
      <Text css={taskDescriptionStyle} color={task.description ? 'default' : 'subdued'}>
        {task.description ? task.description : t('No Description')}
      </Text>
      {locationsData.length > 1 && locationName && (
        <TaskFormRow
          label={t('Location')}
          value={locationName}
          icon='location'
          buttonIconName='location'
          isRemoveable={false}
          isEditing={false}
          isButton={false}
        />
      )}

      <TaskFormRow
        label={t('Patient')}
        icon='user'
        value={patient ? PersonsV3.PersonHelpers.getFullName(patient) : ''}
        placeholder={t('Select Patient')}
        buttonIconName='user'
        leftElement={patient && <Avatar size='xxs' name={PersonsV3.PersonHelpers.getFullName(patient)} />}
        isRemoveable={false}
        isEditing={false}
        isButton={false}
        onClick={onClickEdit}
      />

      <TaskFormRow
        label={t('Due Date')}
        icon='calendar-schedule-back'
        buttonIconName='calendar'
        value={task.dueDate ?? ''}
        placeholder={t('Pick Due Date')}
        isRemoveable={false}
        isEditing={false}
        isButton={false}
        onClick={onClickEdit}
      />

      <TaskFormRow
        label={t('Assignee')}
        icon='patient'
        buttonIconName='user'
        leftElement={assignee && <Avatar isUser size='xxs' name={getFullName(assignee)} />}
        value={assignee ? getFullName(assignee) : ''}
        placeholder={t('Select Assignee')}
        isEditing={false}
        isButton={false}
        onClick={onClickEdit}
      />

      <TaskFormRow
        icon='pending'
        label={t('Status')}
        buttonIconName={taskStatusIcon}
        value={status} // always available
        isRemoveable={false}
        isEditing={false}
        isButton={false}
        onClick={onClickEdit}
      />

      <TaskFormRow
        icon='bar-chart'
        label={t('Priority')}
        buttonIconName={priorityIcon}
        value={taskPriority}
        placeholder={t('No Priority')}
        isRemoveable={false}
        isEditing={false}
        isButton={false}
        onClick={onClickEdit}
        {...(taskPriority === TaskCenterTypes.PriorityType.PRIORITY_HIGH &&
          task.status !== TaskCenterTypes.TaskStatus.STATUS_COMPLETED && { variant: 'critical' })}
      />
    </div>
  );
};
